import { ErrorWithMessageResponse } from '@/models/error-with-message-response/error-with-message-response.model';
import { ElNotification } from 'element-plus';
import { NotificationStatusType } from '@/models/common/notification-status-type.model';

const MIN_HTTP_ERROR_STATUS = 400;
const MAX_HTTP_ERROR_STATUS = 599;

type NotificationWithContextOptions = {
  context?: string;
  fallbackMessage?: string;
  ignoredStatusCodes?: readonly number[];
}

const getErrorMessage = (
  error: unknown,
  fallbackMessage: string,
): string => {
  const err = error as ErrorWithMessageResponse | null | undefined;

  return err?.response?.data?.message ?? err?.message ?? fallbackMessage;
};

const isHttpErrorNotificationAllowed = (
  error: unknown,
  ignoredStatusCodes: readonly number[] = [],
): boolean => {
  const err = error as ErrorWithMessageResponse | null | undefined;
  const code = err?.response?.data?.code || err?.response?.status;

  return typeof code === 'number' &&
    code >= MIN_HTTP_ERROR_STATUS &&
    code <= MAX_HTTP_ERROR_STATUS &&
    !ignoredStatusCodes.includes(code);
};

export const showErrorNotification = (
  error: unknown,
  fallbackMessage = 'Something went wrong',
): void => {
  ElNotification({
    title: 'Error',
    message: getErrorMessage(error, fallbackMessage),
    type: NotificationStatusType.ERROR,
  });
};

export const showErrorNotificationWithContext = (
  error: unknown,
  {
    context,
    fallbackMessage = 'Something went wrong',
    ignoredStatusCodes = [],
  }: NotificationWithContextOptions = {},
): void => {
  if (!isHttpErrorNotificationAllowed(error, ignoredStatusCodes)) {
    return;
  }

  const message = getErrorMessage(error, fallbackMessage);

  ElNotification({
    title: 'Error',
    message: context ? `${message} (${context})` : message,
    type: NotificationStatusType.ERROR,
  });
};

export const showSuccessNotification = (message: string): void => {
  ElNotification({
    title: 'Success',
    message,
    type: NotificationStatusType.SUCCESS,
  });
};
